import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Autocomplete,
  Chip,
  Divider,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import DataTable from "examples/Tables/DataTable";

import crudsService from "services/cruds-service";

const FIELD_LIBRARY = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "contact_number", label: "Contact Number", type: "tel", required: false },
  { key: "org", label: "Organization", type: "text", required: false },
  { key: "message", label: "Message", type: "textarea", required: false },
  { key: "tags", label: "Tags", type: "text", required: false },
  { key: "update", label: "Update Opt-In", type: "checkbox", required: false },
  { key: "address", label: "Address", type: "textarea", required: false },
];

const FIELD_TYPES = ["text", "email", "tel", "textarea", "checkbox", "number", "url"];

const TEMPLATE_LIBRARY = [
  {
    name: "Slide Deck Download",
    subject: "Here are your slides, {{name}}!",
    html: `
<div style="font-family:Arial,sans-serif;line-height:1.6;color:#111827;">
  <h2>Hi {{name}},</h2>
  <p>Thanks for your interest. Click the button below to download the slide deck.</p>
  <p>
    <a href="https://example.com/slides.pdf" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:#1f6feb;color:#fff;padding:12px 20px;border-radius:6px;text-decoration:none;">Download Slides</a>
  </p>
  <p>Best regards,<br />The Team</p>
  <div style="text-align:center;margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;">
    <a href="https://www.quantuniversity.com" target="_blank" rel="noopener noreferrer" style="display:inline-block;">
      <img src="https://www.quantuniversity.com/assets/img/logo5.jpg" alt="QuantUniversity logo" width="90" style="display:block;margin:0 auto;" />
    </a>
    <p style="font-size:12px;color:#6b7280;margin:12px 0 0;">If you did not request this, you can safely ignore this email.</p>
  </div>
</div>`,
  },
  {
    name: "Course Redirect",
    subject: "Your course link, {{name}}",
    html: `
<div style="font-family:Arial,sans-serif;line-height:1.6;color:#111827;">
  <h2>Hello {{name}},</h2>
  <p>Here is the course you requested.</p>
  <p>
    <a href="https://www.quskillbridge.com/courses/example-course" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:#006a9c;color:#fff;padding:12px 20px;border-radius:6px;text-decoration:none;">Open Course</a>
  </p>
  <div style="text-align:center;margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;">
    <a href="https://www.quantuniversity.com" target="_blank" rel="noopener noreferrer" style="display:inline-block;">
      <img src="https://www.quantuniversity.com/assets/img/logo5.jpg" alt="QuantUniversity logo" width="90" style="display:block;margin:0 auto;" />
    </a>
    <p style="font-size:12px;color:#6b7280;margin:12px 0 0;">If you did not request this, you can safely ignore this email.</p>
  </div>
</div>`,
  },
  {
    name: "Simple Thank You",
    subject: "Thank you, {{name}}",
    html: `<div style="font-family:Arial,sans-serif;line-height:1.6;color:#111827;"><p>Hi {{name}},</p><p>Thank you for reaching out. We will follow up shortly.</p><div style="text-align:center;margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;"><a href="https://www.quantuniversity.com" target="_blank" rel="noopener noreferrer" style="display:inline-block;"><img src="https://www.quantuniversity.com/assets/img/logo5.jpg" alt="QuantUniversity logo" width="90" style="display:block;margin:0 auto;" /></a><p style="font-size:12px;color:#6b7280;margin:12px 0 0;">If you did not request this, you can safely ignore this email.</p></div></div>`,
  },
];

const STEPS = ["Endpoint Setup", "Email Content", "Notifications", "Publish & Integrate"];

const notificationColorMap = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
};

const defaultFieldState = (field) => ({
  key: field.key,
  label: field.label,
  type: field.type,
  required: field.required,
  placeholder: `Example ${field.label}`,
  helperText: "",
  options: [],
});

const endpointToFormState = (endpoint) => ({
  id: endpoint?.id || "",
  name: endpoint?.name || "",
  slug: endpoint?.slug || "",
  description: endpoint?.description || "",
  fields:
    endpoint?.fields?.length > 0
      ? endpoint.fields.map((field) => ({
          ...field,
          type: field.type || "text",
          placeholder: field.placeholder || `Example ${field.label || field.key}`,
          helperText: field.helperText || "",
          options: field.options || [],
        }))
      : [defaultFieldState(FIELD_LIBRARY[0]), defaultFieldState(FIELD_LIBRARY[1])],
  follow_up_email_enabled: endpoint?.follow_up_email_enabled || false,
  follow_up_email_subject: endpoint?.follow_up_email_subject || "",
  follow_up_email_html: endpoint?.follow_up_email_html || "",
  notify_on_hit_enabled: endpoint?.notify_on_hit_enabled || false,
  notify_emails: Array.isArray(endpoint?.notify_emails) ? endpoint.notify_emails.join(", ") : "",
  notify_subject: endpoint?.notify_subject || "",
  status: endpoint?.status || "draft",
});

const getApiRoot = () => (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const sanitizeSlugInput = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

const getFieldSampleValue = (field) => {
  if (field.type === "checkbox") {
    return true;
  }
  if (field.type === "email" || field.key === "email") {
    return "alex@example.com";
  }
  if (field.type === "tel" || field.key.includes("contact")) {
    return "+1 617 555 0144";
  }
  if (field.type === "url") {
    return "https://example.com";
  }
  if (field.type === "number") {
    return 42;
  }
  if (field.key === "name") {
    return "Alex Morgan";
  }
  return `Sample ${field.label || field.key}`;
};

const renderTemplatePreview = (template = "", fields = []) => {
  let output = template;
  const replacements = {
    endpoint_name: "Agentic AI Interest Form",
  };

  fields.forEach((field) => {
    replacements[field.key] = String(getFieldSampleValue(field));
  });

  Object.entries(replacements).forEach(([key, value]) => {
    output = output.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}}`, "g"), value);
    output = output.replace(new RegExp(`\\$\\{\\s*${key}\\s*\\}`, "g"), value);
  });

  return output;
};

const CustomFeedbackEndpoints = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpointId, setSelectedEndpointId] = useState("");
  const [formState, setFormState] = useState(endpointToFormState(null));
  const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responses, setResponses] = useState([]);
  const [copiedSnippetTitle, setCopiedSnippetTitle] = useState("");

  const selectedEndpoint = useMemo(
    () => endpoints.find((endpoint) => endpoint.id === selectedEndpointId),
    [endpoints, selectedEndpointId]
  );

  const selectedFieldType = (type) => (FIELD_TYPES.includes(type) ? type : "text");

  const openResponses = async () => {
    if (!formState.id) {
      setNotification({ open: true, message: "Save the endpoint first before viewing responses.", severity: "warning" });
      return;
    }

    setResponsesOpen(true);
    setResponsesLoading(true);
    try {
      const data = await crudsService.getCustomFeedbackEndpointResponses(formState.id);
      setResponses(data || []);
    } catch (error) {
      setNotification({ open: true, message: error?.message || "Failed to load responses.", severity: "error" });
    } finally {
      setResponsesLoading(false);
    }
  };

  const renderStepNavigation = () => (
    <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
      <MDButton
        variant="outlined"
        color="dark"
        disabled={activeStep === 0}
        onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
      >
        Previous
      </MDButton>
      <MDButton
        variant="gradient"
        color="info"
        disabled={activeStep === STEPS.length - 1}
        onClick={() => setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1))}
      >
        Next
      </MDButton>
    </MDBox>
  );

  const copySnippet = async (snippet, title) => {
    try {
      await navigator.clipboard.writeText(snippet);
      setNotification({ open: true, message: "Snippet copied to clipboard.", severity: "success" });
      setCopiedSnippetTitle(title);
      setTimeout(() => setCopiedSnippetTitle(""), 1500);
    } catch (error) {
      setNotification({ open: true, message: "Copy failed. Please try again.", severity: "error" });
    }
  };

  const renderSnippetBlock = (title, code) => (
    <Grid item xs={12}>
      <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
        {title}
      </MDTypography>
      <Box sx={{ position: "relative" }}>
        <IconButton
          size="small"
          onClick={() => copySnippet(code, title)}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, backgroundColor: "rgba(255,255,255,0.9)" }}
        >
          <Icon fontSize="small">{copiedSnippetTitle === title ? "check" : "content_copy"}</Icon>
        </IconButton>
        <Box component="pre" sx={{ p: 2, pr: 5, borderRadius: 1, overflow: "auto", backgroundColor: "grey.100", fontSize: 13 }}>
          {code}
        </Box>
      </Box>
    </Grid>
  );

  const renderResponseValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  const responseTableData = useMemo(() => {
    const rows = responses.map((response) => {
      const payload = response?.payload || {};
      const summary = Object.entries(payload)
        .filter(([, value]) => value !== null && value !== undefined && value !== "")
        .map(([key, value]) => `${key}: ${renderResponseValue(value)}`)
        .join(" | ")
        .slice(0, 180);

      return {
        submitted_at: response.createdAt ? new Date(response.createdAt).toLocaleString() : "-",
        email: response.recipient_email || payload.email || "-",
        response_summary: summary || "-",
        ip_address: response.ip_address || "-",
      };
    });

    return {
      columns: [
        { Header: "submitted at", accessor: "submitted_at", width: "18%" },
        { Header: "email", accessor: "email", width: "22%" },
        { Header: "response summary", accessor: "response_summary", width: "44%" },
        { Header: "ip address", accessor: "ip_address", width: "16%" },
      ],
      rows,
    };
  }, [responses]);

  const publicUrl = useMemo(() => {
    if (!formState.slug) {
      return "";
    }
    return `${getApiRoot()}/feedback/public/${formState.slug}`;
  }, [formState.slug]);

  const dynamicPayloadExample = useMemo(() => {
    if (formState.fields.length === 0) {
      return '{\n  "name": "Alex Morgan"\n}';
    }

    return `{
${formState.fields
  .map((field) => {
    const value = getFieldSampleValue(field);
    return typeof value === "string"
      ? `  "${field.key}": "${value}"`
      : `  "${field.key}": ${JSON.stringify(value)}`;
  })
  .join(",\n")}
}`;
  }, [formState.fields]);

  const dynamicFetchSnippet = useMemo(() => {
    const fallbackUrl = "https://api.example.com/feedback/public/your-slug";
    const endpointUrl = publicUrl || fallbackUrl;

    return `fetch("${endpointUrl}", {
  method: "POST",
  mode: "cors",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(${dynamicPayloadExample}),
});`;
  }, [publicUrl, dynamicPayloadExample]);

  const dynamicHtmlSnippet = useMemo(() => {
    const fallbackUrl = "https://api.example.com/feedback/public/your-slug";
    const endpointUrl = publicUrl || fallbackUrl;

    const fieldHtml = formState.fields
      .map((field) => {
        if (field.type === "checkbox") {
          return `  <label><input type="checkbox" name="${field.key}" ${field.required ? "required" : ""} /> ${
            field.label
          }</label>`;
        }
        if (field.type === "textarea") {
          return `  <textarea name="${field.key}" placeholder="${field.placeholder || field.label}" ${
            field.required ? "required" : ""
          }></textarea>`;
        }
        return `  <input type="${field.type || "text"}" name="${field.key}" placeholder="${
          field.placeholder || field.label
        }" ${field.required ? "required" : ""} />`;
      })
      .join("\n");

    return `<!-- ${formState.name || "Custom Feedback Form"} -->
<form id="custom-feedback-form">
${fieldHtml || "  <input name=\"name\" placeholder=\"Name\" />"}
  <button type="submit">Submit</button>
</form>

<script>
document.getElementById("custom-feedback-form").addEventListener("submit", async (event) => {
  event.preventDefault();
  const formData = new FormData(event.target);
  const payload = {};
  formData.forEach((value, key) => {
    payload[key] = value;
  });

  ${formState.fields
    .filter((field) => field.type === "checkbox")
    .map((field) => `payload["${field.key}"] = event.target.elements["${field.key}"].checked;`)
    .join("\n  ")}

  await fetch("${endpointUrl}", {
    method: "POST",
    mode: "cors",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
});
</script>`;
  }, [formState.fields, formState.name, publicUrl]);

  const refreshEndpoints = async () => {
    const data = await crudsService.getCustomFeedbackEndpoints();
    setEndpoints(data || []);
  };

  useEffect(() => {
    refreshEndpoints().catch((error) => {
      setNotification({
        open: true,
        message: error?.message || "Failed to load endpoints.",
        severity: "error",
      });
    });
  }, []);

  useEffect(() => {
    if (selectedEndpoint) {
      setFormState(endpointToFormState(selectedEndpoint));
      setActiveStep(0);
    }
  }, [selectedEndpoint]);

  useEffect(() => {
    if (!notification.open) {
      return;
    }

    const timer = setTimeout(() => {
      setNotification((prev) => ({ ...prev, open: false }));
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.open]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSlugChange = (event) => {
    setFormState((prev) => ({
      ...prev,
      slug: sanitizeSlugInput(event.target.value),
    }));
  };

  const handleFieldChange = (index, name, value) => {
    setFormState((prev) => {
      const fields = [...prev.fields];
      const updated = { ...fields[index], [name]: value };

      if (name === "type" && !FIELD_TYPES.includes(value)) {
        updated.type = "text";
      }

      fields[index] = updated;
      return { ...prev, fields };
    });
  };

  const toggleLibraryField = (field) => {
    setFormState((prev) => {
      const exists = prev.fields.some((item) => item.key === field.key);
      if (exists) {
        return { ...prev, fields: prev.fields.filter((item) => item.key !== field.key) };
      }
      return { ...prev, fields: [...prev.fields, defaultFieldState(field)] };
    });
  };

  const addCustomField = () => {
    setFormState((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          key: `custom_field_${prev.fields.length + 1}`,
          label: "Custom Field",
          type: "text",
          required: false,
          placeholder: "Example custom input",
          helperText: "",
          options: [],
        },
      ],
    }));
  };

  const removeField = (index) => {
    setFormState((prev) => ({ ...prev, fields: prev.fields.filter((_, fieldIndex) => fieldIndex !== index) }));
  };

  const loadTemplate = (template) => {
    setFormState((prev) => ({
      ...prev,
      follow_up_email_subject: template.subject,
      follow_up_email_html: template.html,
      follow_up_email_enabled: true,
    }));
  };

  const resetForm = () => {
    setSelectedEndpointId("");
    setFormState(endpointToFormState(null));
    setActiveStep(0);
  };

  const saveEndpoint = async () => {
    setLoading(true);
    try {
      const payload = {
        name: formState.name,
        slug: formState.slug,
        description: formState.description,
        fields: formState.fields,
        follow_up_email_enabled: formState.follow_up_email_enabled,
        follow_up_email_subject: formState.follow_up_email_subject,
        follow_up_email_html: formState.follow_up_email_html,
        notify_on_hit_enabled: formState.notify_on_hit_enabled,
        notify_emails: formState.notify_emails
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        notify_subject: formState.notify_subject,
        status: formState.status,
      };

      let saved;
      if (formState.id) {
        saved = await crudsService.updateCustomFeedbackEndpoint(formState.id, payload);
      } else {
        saved = await crudsService.createCustomFeedbackEndpoint(payload);
      }

      setFormState(endpointToFormState(saved));
      setSelectedEndpointId(saved.id);
      await refreshEndpoints();
      setNotification({ open: true, message: "Endpoint saved successfully.", severity: "success" });
    } catch (error) {
      setNotification({ open: true, message: error?.message || "Failed to save endpoint.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const publishEndpoint = async () => {
    if (!formState.id) {
      return;
    }

    setLoading(true);
    try {
      const saved = await crudsService.publishCustomFeedbackEndpoint(formState.id);
      setFormState(endpointToFormState(saved));
      await refreshEndpoints();
      setNotification({ open: true, message: "Endpoint published.", severity: "success" });
    } catch (error) {
      setNotification({ open: true, message: error?.message || "Failed to publish endpoint.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const unpublishEndpoint = async () => {
    if (!formState.id) {
      return;
    }

    setLoading(true);
    try {
      const saved = await crudsService.unpublishCustomFeedbackEndpoint(formState.id);
      setFormState(endpointToFormState(saved));
      await refreshEndpoints();
      setNotification({ open: true, message: "Endpoint unpublished.", severity: "info" });
    } catch (error) {
      setNotification({ open: true, message: error?.message || "Failed to unpublish endpoint.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const deleteEndpoint = async () => {
    if (!formState.id) {
      return;
    }

    setLoading(true);
    try {
      await crudsService.deleteCustomFeedbackEndpoint(formState.id);
      resetForm();
      await refreshEndpoints();
      setNotification({ open: true, message: "Endpoint deleted.", severity: "success" });
    } catch (error) {
      setNotification({ open: true, message: error?.message || "Failed to delete endpoint.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack spacing={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Endpoint Basics
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Endpoint Name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="Boston FinTech Week Contact Form"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Endpoint Slug"
                    name="slug"
                    value={formState.slug}
                    onChange={handleSlugChange}
                    placeholder="boston-fintech-week-2026"
                    helperText="Used in URL: /feedback/public/<slug>"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    name="description"
                    value={formState.description}
                    onChange={handleChange}
                    placeholder="Collect lead details and send conference follow-up resources."
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Card>

          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Field Builder
              </MDTypography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {FIELD_LIBRARY.map((field) => {
                  const selected = formState.fields.some((item) => item.key === field.key);
                  return (
                    <Grid item key={field.key}>
                      <Chip
                        label={`${field.label}${selected ? " ✓" : ""}`}
                        color={selected ? "info" : "default"}
                        onClick={() => toggleLibraryField(field)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              <MDButton variant="outlined" color="info" onClick={addCustomField}>
                Add Custom Field
              </MDButton>

              <Stack spacing={2} mt={2}>
                {formState.fields.map((field, index) => (
                  <Card key={`${field.key}-${index}`} variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>{field.label}</Typography>
                        <IconButton onClick={() => removeField(index)} size="small">
                          <Icon>delete</Icon>
                        </IconButton>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Key"
                            value={field.key}
                            onChange={(event) => handleFieldChange(index, "key", event.target.value)}
                            placeholder="company_name"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Label"
                            value={field.label}
                            onChange={(event) => handleFieldChange(index, "label", event.target.value)}
                            placeholder="Company Name"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            id={`field-type-${index}`}
                            options={FIELD_TYPES}
                            value={selectedFieldType(field.type)}
                            onChange={(event, value) => handleFieldChange(index, "type", value || "text")}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Type"
                                placeholder="Select type"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Placeholder"
                            value={field.placeholder || ""}
                            onChange={(event) => handleFieldChange(index, "placeholder", event.target.value)}
                            placeholder="Example: Acme Capital"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Helper Text"
                            value={field.helperText || ""}
                            onChange={(event) => handleFieldChange(index, "helperText", event.target.value)}
                            placeholder="Optional hint shown under the field"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Switch
                              checked={!!field.required}
                              onChange={(event) => handleFieldChange(index, "required", event.target.checked)}
                            />
                            <Typography variant="body2">Required</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </MDBox>
          </Card>
        </Stack>
      );
    }

    if (activeStep === 1) {
      return (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Follow-up Email Builder
            </MDTypography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Switch
                checked={formState.follow_up_email_enabled}
                name="follow_up_email_enabled"
                onChange={handleChange}
              />
              <Typography>Send follow-up email to submitter</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    name="follow_up_email_subject"
                    value={formState.follow_up_email_subject}
                    onChange={handleChange}
                    placeholder="Thanks {{name}} - here are your resources"
                  />

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={14}
                      label="Email Body HTML"
                      name="follow_up_email_html"
                      value={formState.follow_up_email_html}
                      onChange={handleChange}
                      placeholder="<h2>Hello {{name}}</h2><p>Thanks for attending...</p>"
                    />
                    <Stack direction="row" spacing={1} mt={2} useFlexGap flexWrap="wrap">
                      {TEMPLATE_LIBRARY.map((template) => (
                        <Chip key={template.name} label={template.name} onClick={() => loadTemplate(template)} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Live Preview
                </MDTypography>
                <Box
                  sx={{
                    border: "1px solid #d2d6da",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    minHeight: 420,
                    backgroundColor: "#fff",
                  }}
                >
                  <iframe
                    title="email-preview"
                    style={{ width: "100%", minHeight: 420, border: "none" }}
                    sandbox="allow-same-origin"
                    srcDoc={renderTemplatePreview(formState.follow_up_email_html || "", formState.fields)}
                  />
                </Box>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      );
    }

    if (activeStep === 2) {
      return (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Hit Notifications
            </MDTypography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Switch
                checked={formState.notify_on_hit_enabled}
                name="notify_on_hit_enabled"
                onChange={handleChange}
              />
              <Typography>Notify internal recipients when this endpoint is hit</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notification Emails"
                  name="notify_emails"
                  value={formState.notify_emails}
                  onChange={handleChange}
                  helperText="Comma-separated list (e.g. team@company.com, ops@company.com)"
                  placeholder="team@quantuniversity.com, sales@quantuniversity.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notification Subject"
                  name="notify_subject"
                  value={formState.notify_subject}
                  onChange={handleChange}
                  placeholder="New submission: {{name}}"
                />
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      );
    }

    return (
      <Stack spacing={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Publish and Integration
            </MDTypography>
            <Typography variant="body2" color="text.secondary">
              Endpoint name: <strong>{formState.name || "(not set)"}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Public endpoint: <strong>{publicUrl || "Save endpoint with slug to generate URL"}</strong>
            </Typography>

            <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
              Required Fields
            </MDTypography>
            <Stack direction="row" spacing={1} mb={2} useFlexGap flexWrap="wrap">
              {formState.fields.filter((field) => field.required).length > 0 ? (
                formState.fields
                  .filter((field) => field.required)
                  .map((field) => <Chip key={field.key} label={`${field.label} (${field.key})`} color="info" />)
              ) : (
                <Chip label="No required fields" />
              )}
            </Stack>

            <Grid container spacing={2}>
              {renderSnippetBlock("Dynamic JSON payload example", dynamicPayloadExample)}
              {renderSnippetBlock("Static website fetch example (dynamic)", dynamicFetchSnippet)}
              {renderSnippetBlock("Static HTML form + submit script (dynamic)", dynamicHtmlSnippet)}
            </Grid>
          </MDBox>
        </Card>

      </Stack>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDTypography variant="h3" mt={2}>
        Feedback Endpoints
      </MDTypography>
      <Divider />

      {notification.open && (
        <MDAlert color={notificationColorMap[notification.severity] || "info"} my="20px">
          <MDTypography variant="body2" color="white">
            {notification.message}
          </MDTypography>
        </MDAlert>
      )}

      <MDBox pt={2} pb={3}>
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  id="existing-endpoint-autocomplete"
                  options={endpoints}
                  value={selectedEndpoint || null}
                  getOptionLabel={(option) => `${option?.name || ""}${option?.slug ? ` (${option.slug})` : ""}`}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(event, value) => setSelectedEndpointId(value?.id || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Edit Existing Endpoint"
                      placeholder="Create a new endpoint"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, overflowX: "auto" }}
                  useFlexGap
                >
                  <MDButton size="small" variant="gradient" color="dark" onClick={openResponses} disabled={!formState.id}>
                    View Responses
                  </MDButton>
                  <MDButton size="small" variant="gradient" color="dark" onClick={resetForm}>
                    New Endpoint
                  </MDButton>
                  <MDButton size="small" variant="gradient" color="info" onClick={saveEndpoint} disabled={loading}>
                    Save
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="gradient"
                    color="success"
                    onClick={publishEndpoint}
                    disabled={loading || !formState.id || formState.status === "published"}
                  >
                    Publish
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="gradient"
                    color="warning"
                    onClick={unpublishEndpoint}
                    disabled={loading || !formState.id || formState.status === "draft"}
                  >
                    Unpublish
                  </MDButton>
                  <MDButton size="small" variant="gradient" color="error" onClick={deleteEndpoint} disabled={loading || !formState.id}>
                    Delete
                  </MDButton>
                </Stack>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <MDBox mt={3}>{renderStepNavigation()}</MDBox>
          </MDBox>
        </Card>

        {renderStepContent()}

        <MDBox mt={3} mb={1}>
          {renderStepNavigation()}
        </MDBox>
      </MDBox>

      <Modal open={responsesOpen} onClose={() => setResponsesOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92vw", md: "80vw" },
            maxHeight: "85vh",
            overflow: "auto",
            backgroundColor: "#fff",
            borderRadius: 2,
            p: 3,
            boxShadow: 24,
          }}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <MDTypography variant="h5" fontWeight="medium">
                Endpoint Responses
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                {formState.name || "Current endpoint"}
              </MDTypography>
            </Box>
            <IconButton onClick={() => setResponsesOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          {responsesLoading ? (
            <MDTypography variant="body2">Loading responses...</MDTypography>
          ) : responses.length === 0 ? (
            <MDTypography variant="body2" color="text.secondary">
              No responses yet.
            </MDTypography>
          ) : (
            <Card sx={{ boxShadow: "none" }}>
              <MDBox p={0}>
                <DataTable table={responseTableData} canSearch entriesPerPage={false} showTotalEntries={false} noEndBorder />
              </MDBox>
            </Card>
          )}
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default CustomFeedbackEndpoints;
